import { useState, useEffect, useCallback } from 'react';
import { Modal, useModalClose } from '@/components/ui/Modal';
import type { ButtonHTMLAttributes } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, CheckCircle2, XCircle, ArrowRightCircle, Ban,
  Clock, Package, FileCheck, Link2, AlertTriangle, X,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';
import { VendorPicker, type SupplierLink } from './VendorPicker';
import { loadVendorOptions, type VendorOption } from './vendorOptions';

// ==================== Types ====================

interface VendorHistoryItem {
  vendorName: string;
  rate: number;
  date: string;
  quantity: number;
}

interface PurchaseRequestDetail {
  id: string;
  number: string;
  status: string;
  urgency: string;
  productId: string;
  productName: string;
  product?: { id: string; name: string; sku: string };
  quantity: number;
  unit: string;
  reason: string;
  notes: string;
  estimatedRate: number | null;
  estimatedTotal: number | null;
  hasActiveContract: boolean;
  contractId: string | null;
  purchaseOrderId: string | null;
  purchaseOrder?: { id: string; number: string };
  requestedBy: string;
  /** Name of the person behind requestedBy (a user id); null for old rows that stored a name. */
  requesterName?: string | null;
  approvedBy: string | null;
  approverName?: string | null;
  approvedAt: string | null;
  rejectedReason: string | null;
  vendorHistory: VendorHistoryItem[];
  createdAt: string;
  updatedAt: string;
}

// ==================== Component ====================

export function PurchaseRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [pr, setPr] = useState<PurchaseRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Reject modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Convert modal
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertVendor, setConvertVendor] = useState('');
  const [convertRate, setConvertRate] = useState('');
  const [convertVendors, setConvertVendors] = useState<VendorOption[]>([]);
  const [convertSuppliers, setConvertSuppliers] = useState<SupplierLink[]>([]);
  const [convertHint, setConvertHint] = useState<string | null>(null);
  const [convertLoadError, setConvertLoadError] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/purchase-requests/${id}`);
      setPr(data.data ?? data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load purchase request');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  // SUN-239: the vendor dropdown. Suppliers of this material come first; the preferred one is pre-selected.
  const productId = pr?.productId;
  const requestUnit = pr?.unit;
  useEffect(() => {
    if (!showConvertModal || !productId) return;
    const ctrl = new AbortController();
    setConvertLoadError(null);
    (async () => {
      try {
        const [vendors, suppliers] = await Promise.all([
          loadVendorOptions(ctrl.signal),
          api.get(`/vendors/products/by-product/${productId}`, { signal: ctrl.signal })
            .then((r) => (Array.isArray(r.data?.data) ? r.data.data : []) as any[])
            .catch(() => [] as any[]), // 503 VENDOR_PRODUCTS_NOT_READY or no access: no supplier list
        ]);
        if (ctrl.signal.aborted) return;
        const links: SupplierLink[] = suppliers.map((l) => ({
          vendorId: l.vendorId, name: l.vendor?.name ?? '', gstin: l.vendor?.gstin ?? null,
          rate: l.rate == null ? null : Number(l.rate), unit: l.unit ?? null, isPreferred: !!l.isPreferred,
        }));
        setConvertVendors(vendors);
        setConvertSuppliers(links);
        const preferred = links.find((l) => l.isPreferred && vendors.some((v) => v.id === l.vendorId));
        if (preferred) applyVendor(preferred.vendorId, preferred, requestUnit);
      } catch (err: any) {
        if (!ctrl.signal.aborted) setConvertLoadError(err?.message || 'Could not load vendors');
      }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showConvertModal, productId]);

  /** Pick a vendor; fill its quoted rate only when the quote is in the request's unit (or has no unit). */
  const applyVendor = (vendorId: string, supplier: SupplierLink | null, unit: string | undefined) => {
    setConvertVendor(vendorId);
    setConvertHint(null);
    setConvertRate(''); // SUN-285: a new vendor replaces the old vendor's rate; it never lingers
    if (!supplier || supplier.rate == null) return;
    const q = (supplier.unit || '').trim().toLowerCase();
    if (!q || q === (unit || '').trim().toLowerCase()) setConvertRate(String(supplier.rate));
    else setConvertHint(`Quoted ${formatINR(supplier.rate)} per ${supplier.unit}`);
  };

  const handleApprove = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await api.post(`/purchase-requests/${id}/approve`);
      await fetchDetail();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!id || !rejectReason.trim()) return;
    setActionLoading(true);
    try {
      await api.post(`/purchase-requests/${id}/reject`, { rejectedReason: rejectReason });
      setShowRejectModal(false);
      setRejectReason('');
      await fetchDetail();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!id || !confirm('Cancel this purchase request?')) return;
    setActionLoading(true);
    try {
      await api.post(`/purchase-requests/${id}/cancel`);
      await fetchDetail();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to cancel');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConvert = async () => {
    if (!id || !convertVendor.trim() || !convertRate) return;
    setActionLoading(true);
    try {
      await api.post(`/purchase-requests/${id}/convert`, {
        vendorId: convertVendor,
        rate: Number(convertRate),
      });
      setShowConvertModal(false);
      setConvertVendor('');
      setConvertRate('');
      setConvertHint(null);
      await fetchDetail();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to convert');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error || !pr) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 dark:text-red-400 mb-4">{error || 'Not found'}</p>
        <button onClick={() => navigate('/purchase-requests')} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          Back to List
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/purchase-requests')}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{pr.number}</h1>
            <StatusBadge status={pr.status} />
            <UrgencyBadge urgency={pr.urgency} />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {pr.productName} &middot; Created {formatDate(pr.createdAt)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Product Info Card */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Package className="w-4 h-4 text-gray-500" />
              Product Information
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <InfoField label="Product" value={pr.productName} />
              <InfoField label="SKU" value={pr.product?.sku || '—'} />
            </div>
          </div>

          {/* Request Details Card */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-500" />
              Request Details
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <InfoField label="Quantity" value={`${pr.quantity} ${pr.unit}`} />
              <InfoField label="Estimated Rate" value={pr.estimatedRate != null ? formatINR(pr.estimatedRate) : '—'} />
              <InfoField label="Estimated Total" value={pr.estimatedTotal != null ? formatINR(pr.estimatedTotal) : '—'} />
              <InfoField label="Requested By" value={pr.requesterName || pr.requestedBy} />
            </div>
            {pr.reason && (
              <div className="mt-4">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Reason</p>
                <p className="text-sm text-gray-900 dark:text-white">{pr.reason}</p>
              </div>
            )}
            {pr.notes && (
              <div className="mt-3">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Notes</p>
                <p className="text-sm text-gray-700 dark:text-gray-300">{pr.notes}</p>
              </div>
            )}
            {pr.rejectedReason && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-1">Rejection Reason</p>
                <p className="text-sm text-red-800 dark:text-red-300">{pr.rejectedReason}</p>
              </div>
            )}
          </div>

          {/* Vendor History */}
          {pr.vendorHistory && pr.vendorHistory.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Vendor History</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Previous purchase orders for this product</p>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Vendor</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Rate</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Qty</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {pr.vendorHistory.map((vh, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2 text-gray-900 dark:text-white">{vh.vendorName}</td>
                      <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{formatINR(vh.rate)}</td>
                      <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{vh.quantity}</td>
                      <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{formatDate(vh.date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Action Buttons */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Actions</h3>
            {pr.status === 'pending' && (
              <>
                <button
                  onClick={handleApprove}
                  disabled={actionLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Approve
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  disabled={actionLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
                >
                  <XCircle className="w-4 h-4" />
                  Reject
                </button>
                <button
                  onClick={handleCancel}
                  disabled={actionLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 text-sm font-medium"
                >
                  <Ban className="w-4 h-4" />
                  Cancel Request
                </button>
              </>
            )}
            {pr.status === 'approved' && (
              <>
                <button
                  onClick={() => setShowConvertModal(true)}
                  disabled={actionLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
                >
                  <ArrowRightCircle className="w-4 h-4" />
                  Convert to PO
                </button>
                <button
                  onClick={handleCancel}
                  disabled={actionLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 text-sm font-medium"
                >
                  <Ban className="w-4 h-4" />
                  Cancel Request
                </button>
              </>
            )}
            {pr.status === 'converted' && pr.purchaseOrderId && (
              <button
                onClick={() => navigate(`/purchase-orders/${pr.purchaseOrderId}/edit`)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                <Link2 className="w-4 h-4" />
                View PO {pr.purchaseOrder?.number || ''}
              </button>
            )}
            {(pr.status === 'rejected' || pr.status === 'cancelled') && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                No actions available
              </p>
            )}
          </div>

          {/* Contract Info */}
          {pr.hasActiveContract && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <FileCheck className="w-4 h-4 text-green-500" />
                Active Contract
              </h3>
              <div className="space-y-3">
                <InfoField label="Contract ID" value={pr.contractId || '—'} />
                <InfoField label="Status" value="Contract linked" />
              </div>
            </div>
          )}

          {/* Linked PO */}
          {pr.purchaseOrderId && (
            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 p-5">
              <h3 className="text-sm font-semibold text-green-800 dark:text-green-300 mb-2 flex items-center gap-2">
                <Link2 className="w-4 h-4" />
                Linked Purchase Order
              </h3>
              <p className="text-sm text-green-700 dark:text-green-400">{pr.purchaseOrder?.number || pr.purchaseOrderId}</p>
              <button
                onClick={() => navigate(`/purchase-orders/${pr.purchaseOrderId}/edit`)}
                className="mt-2 text-xs text-green-600 dark:text-green-400 underline hover:no-underline"
              >
                Open PO
              </button>
            </div>
          )}

          {/* Timeline */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Timeline</h3>
            <div className="space-y-3">
              <TimelineItem label="Created" date={pr.createdAt} by={pr.requesterName || pr.requestedBy} />
              {pr.approvedAt && <TimelineItem label="Approved" date={pr.approvedAt} by={pr.approverName || pr.approvedBy} />}
              {pr.status === 'rejected' && <TimelineItem label="Rejected" date={pr.updatedAt} />}
              {pr.status === 'converted' && <TimelineItem label="Converted to PO" date={pr.updatedAt} />}
              {pr.status === 'cancelled' && <TimelineItem label="Cancelled" date={pr.updatedAt} />}
            </div>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <Modal open onClose={() => setShowRejectModal(false)} className="!max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Reject Request</h2>
              <RequestCloseButton className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </RequestCloseButton>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason for rejection</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                placeholder="Please provide a reason..."
              />
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <RequestCloseButton
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </RequestCloseButton>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim() || actionLoading}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Reject
              </button>
            </div>
        </Modal>
      )}

      {/* Convert Modal */}
      {showConvertModal && (
        <Modal open onClose={() => setShowConvertModal(false)} className="!max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Convert to Purchase Order</h2>
              <RequestCloseButton className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </RequestCloseButton>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Vendor</label>
                <VendorPicker
                  vendors={convertVendors}
                  suppliers={convertSuppliers}
                  value={convertVendor}
                  onChange={(id, supplier) => applyVendor(id, supplier, pr.unit)}
                />
                {convertLoadError && <p className="mt-1 text-xs text-red-600">{convertLoadError}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rate per {pr.unit}</label>
                <input
                  type="number"
                  value={convertRate}
                  onChange={(e) => setConvertRate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
                {convertHint && <p className="mt-1 text-xs text-orange-600">{convertHint}</p>}
              </div>
              {convertRate && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Estimated PO Value</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {formatINR(pr.quantity * Number(convertRate))}
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <RequestCloseButton
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </RequestCloseButton>
              <button
                onClick={handleConvert}
                disabled={!convertVendor.trim() || !convertRate || actionLoading}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Create PO
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
}

// ==================== Sub Components ====================

function InfoField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-sm text-gray-900 dark:text-white mt-0.5">{value}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
    approved: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    converted: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    rejected: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    cancelled: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
  };
  return (
    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${styles[status] || styles.pending}`}>
      {status.toUpperCase()}
    </span>
  );
}

function UrgencyBadge({ urgency }: { urgency: string }) {
  const styles: Record<string, string> = {
    low: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
    medium: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    high: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
    critical: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  };
  return (
    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${styles[urgency] || styles.medium}`}>
      {urgency.toUpperCase()}
    </span>
  );
}

function TimelineItem({ label, date, by }: { label: string; date: string; by?: string | null }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-2 h-2 mt-1.5 rounded-full bg-blue-500 flex-shrink-0" />
      <div>
        <p className="text-sm text-gray-900 dark:text-white">{label}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {formatDate(date)}{by ? ` by ${by}` : ''}
        </p>
      </div>
    </div>
  );
}

function RequestCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}
